import { isThirdPlaceLabel, knockoutStageOrder } from "./bracketFromMatches";

/**
 * Reconstrucción del cuadro completo del sorteo, línea por línea (pedido de
 * Eduardo 2026-07-22): la fuente de verdad son los partidos de la PRIMERA
 * ronda ya generados — el contrato del generador de eliminación es que el
 * match m ocupa las líneas 2m-1 (player1) y 2m (player2) — y el acta del
 * DrawCeremony aporta las bandas de los sembrados (línea → seed/banda).
 *
 * Todo puro (sin React ni fetch) para poder testearlo en Vitest.
 */

/** Subconjunto estructural de TournamentMatch que necesita la reconstrucción
 * (los tests arman este shape sin cargar el tipo completo del API). */
export interface DrawLineSourceMatch {
  id: number;
  /** Los CANCELADOS se excluyen de la reconstrucción: un cuadro regenerado
   * deja el partido viejo cancelado en la misma ronda y duplicaría líneas
   * (mismo criterio que el resto de derivaciones del gate #136). Opcional
   * para tolerar payloads estructurales sin status. */
  status?: string | null;
  /** Notas del generador: detectan la ronda inicial PARCIAL de eliminación
   * ("Ronda inicial parcial: N jugador(es) pasan directo al cuadro de M."),
   * que el backend etiqueta con la ronda canónica (p.ej. "Round of 16") pero
   * NO es el cuadro completo. */
  notes?: string | null;
  /** El backend representa un BYE real también como status "completed" +
   * score_text "BYE" (con un solo participante). Opcional en el shape. */
  score_text?: string | null;
  round_label: string | null;
  match_number: number | null;
  player1_participant_id: number | null;
  player2_participant_id: number | null;
  player1: { display_name: string | null } | null;
  player2: { display_name: string | null } | null;
}

/** Subconjunto estructural de DrawCeremony (el acta puede faltar: cuadros
 * generados antes de que existiera, o vistas sin ese payload). */
export interface DrawCeremonyActaSource {
  payload?: {
    bands?: Array<{
      band: string;
      drawn: boolean;
      seeds: Array<{
        seed: number;
        line: number | null;
        participant_id: number;
        display_name: string;
      }>;
    }>;
  } | null;
}

export interface DrawCeremonyLine {
  line: number;
  /** null = la línea no tiene partido generado (hueco en la numeración). */
  matchNumber: number | null;
  participantId: number | null;
  /** null con matchNumber ≠ null = BYE (lugar vacío en un partido real). */
  displayName: string | null;
  /** #1..#N del acta; null = no sembrado (o cuadro sin acta). */
  seed: number | null;
  /** Banda del acta ("1", "2", "3-4", "5-8"…); null = sin banda. */
  band: string | null;
  /** true = la banda se sorteó entre sus líneas; false = línea fija. */
  bandDrawn: boolean | null;
  /** true SOLO cuando el partido es un BYE real (status "bye"); un lado vacío
   * de un partido normal (esperando ganador de preliminar) NO es BYE. */
  isBye: boolean;
}

export interface DrawCeremonyLinesResult {
  /** Etiqueta cruda de la ronda que ES el cuadro completo ("Round of 16"…). */
  roundLabel: string | null;
  lines: DrawCeremonyLine[];
  /** Ronda preliminar (si existe): sus partidos se listan APARTE — con pases
   * directos, la preliminar NO es el cuadro completo y presentarla como tal
   * mentiría (auditoría del gate de #135). */
  preliminaryRoundLabel: string | null;
  preliminaryLines: DrawCeremonyLine[];
  /** true cuando SOLO existe la ronda preliminar/parcial: el cuadro principal
   * aún no se genera y NADA debe presentarse como "cuadro completo". */
  mainBracketPending: boolean;
  /** Cuántas líneas quedaron marcadas como sembradas desde el acta. */
  seededCount: number;
  /** true cuando el acta traía bandas (permite distinguir "no sembrado"
   * de "cuadro sin acta" al pintar la columna Banda). */
  hasActaBands: boolean;
}

function sortByNumber(a: DrawLineSourceMatch, b: DrawLineSourceMatch): number {
  const aNumber = a.match_number ?? 1_000_000;
  const bNumber = b.match_number ?? 1_000_000;
  return aNumber - bNumber || a.id - b.id;
}

/** Los match_number solo son confiables como número de línea si todos existen,
 * son positivos y no se repiten; si no, se numera secuencialmente. */
function effectiveMatchNumbers(matches: DrawLineSourceMatch[]): number[] {
  const raw = matches.map((match) => match.match_number);
  const positive = raw.filter(
    (value): value is number => typeof value === "number" && Number.isInteger(value) && value >= 1,
  );
  // Acotado (gate de #135): un match_number basura (100000, 2^31) no puede
  // dictar el tamaño del arreglo de líneas — máximo 128 partidos (256 líneas);
  // fuera de rango se cae a la numeración secuencial.
  const usable =
    positive.length === matches.length &&
    new Set(positive).size === matches.length &&
    Math.max(...positive) <= 128;
  return usable ? (raw as number[]) : matches.map((_, index) => index + 1);
}

export function buildDrawCeremonyLines(
  matches: DrawLineSourceMatch[],
  ceremony: DrawCeremonyActaSource | null | undefined,
): DrawCeremonyLinesResult {
  // Primera ronda del cuadro: la etiqueta knockout con orden más bajo
  // (Preliminar < Ronda de 128 < … < Final); 3er lugar y etiquetas ajenas
  // (Jornada N de round robin) quedan fuera.
  const buckets = new Map<number, { label: string | null; matches: DrawLineSourceMatch[] }>();
  for (const match of matches) {
    if ((match.status ?? "").trim().toLowerCase() === "cancelled") continue;
    if (isThirdPlaceLabel(match.round_label)) continue;
    const order = knockoutStageOrder(match.round_label);
    if (order === null) continue;
    const bucket = buckets.get(order) ?? { label: match.round_label, matches: [] };
    bucket.matches.push(match);
    buckets.set(order, bucket);
  }
  const bands = ceremony?.payload?.bands ?? [];
  if (buckets.size === 0) {
    return {
      roundLabel: null,
      lines: [],
      preliminaryRoundLabel: null,
      preliminaryLines: [],
      mainBracketPending: false,
      seededCount: 0,
      hasActaBands: bands.length > 0,
    };
  }

  // Acta → sembrados: se cruza por participant_id (sobrevive a un cuadro
  // rearmado donde las líneas del acta ya no coinciden 1:1 con los matches).
  const seedByParticipant = new Map<number, { seed: number; band: string; drawn: boolean }>();
  for (const band of bands) {
    for (const row of band.seeds) {
      seedByParticipant.set(row.participant_id, {
        seed: row.seed,
        band: band.band,
        drawn: band.drawn,
      });
    }
  }

  const linesForRound = (bucket: { matches: DrawLineSourceMatch[] }): DrawCeremonyLine[] => {
    const roundMatches = [...bucket.matches].sort(sortByNumber);
    const numbers = effectiveMatchNumbers(roundMatches);
    const lineCount = Math.min(Math.max(...numbers) * 2, 256);
    const lines: DrawCeremonyLine[] = Array.from({ length: lineCount }, (_, index) => ({
      line: index + 1,
      matchNumber: null,
      participantId: null,
      displayName: null,
      seed: null,
      band: null,
      bandDrawn: null,
      isBye: false,
    }));
    roundMatches.forEach((match, index) => {
      const matchNumber = numbers[index];
      if (matchNumber * 2 > lineCount) return; // defensa del tope de 256
      const sides = [
        { line: matchNumber * 2 - 1, participantId: match.player1_participant_id, brief: match.player1 },
        { line: matchNumber * 2, participantId: match.player2_participant_id, brief: match.player2 },
      ];
      for (const side of sides) {
        const entry = lines[side.line - 1];
        entry.matchNumber = matchNumber;
        entry.isBye =
          (match.status ?? "").trim().toLowerCase() === "bye" ||
          (match.score_text ?? "").trim().toUpperCase() === "BYE";
        entry.participantId = side.participantId;
        entry.displayName =
          side.brief?.display_name?.trim() || (side.participantId !== null ? `#${side.participantId}` : null);
        const seedInfo = side.participantId !== null ? seedByParticipant.get(side.participantId) : undefined;
        if (seedInfo) {
          entry.seed = seedInfo.seed;
          entry.band = seedInfo.band;
          entry.bandDrawn = seedInfo.drawn;
        }
      }
    });
    return lines;
  };

  // Ronda "de entrada" que NO es el cuadro completo: la Preliminar (orden 0)
  // o una ronda inicial PARCIAL de eliminación (nota estable del generador:
  // "Ronda inicial parcial: … pasan directo…", etiquetada con la ronda
  // canónica pero con pases directos que no juegan en ella).
  const isEntryOnlyRound = (bucket: { matches: DrawLineSourceMatch[] }, order: number): boolean =>
    order === 0 || bucket.matches.some((match) => /Ronda inicial parcial:/.test(match.notes ?? ""));

  const orders = [...buckets.keys()].sort((a, b) => a - b);
  const mainOrder = orders.find((order) => !isEntryOnlyRound(buckets.get(order)!, order));
  const entryOrder = orders.find((order) => isEntryOnlyRound(buckets.get(order)!, order));
  const preliminaryRound = entryOrder !== undefined && mainOrder !== undefined && entryOrder < mainOrder
    ? buckets.get(entryOrder)!
    : null;

  if (mainOrder === undefined) {
    // SOLO hay ronda(s) de entrada: nada puede presentarse como "cuadro
    // completo" — la ronda se lista como preliminar/parcial y el cuadro
    // principal queda explícitamente pendiente (gate de #135).
    const entryRound = buckets.get(entryOrder!)!;
    const preliminaryLines = linesForRound(entryRound);
    return {
      roundLabel: null,
      lines: [],
      preliminaryRoundLabel: entryRound.label,
      preliminaryLines,
      mainBracketPending: true,
      seededCount: preliminaryLines.filter((line) => line.seed !== null).length,
      hasActaBands: bands.length > 0,
    };
  }

  const mainRound = buckets.get(mainOrder)!;
  const lines = linesForRound(mainRound);
  const preliminaryLines = preliminaryRound ? linesForRound(preliminaryRound) : [];

  return {
    roundLabel: mainRound.label,
    lines,
    preliminaryRoundLabel: preliminaryRound ? preliminaryRound.label : null,
    preliminaryLines,
    mainBracketPending: false,
    seededCount:
      lines.filter((line) => line.seed !== null).length +
      preliminaryLines.filter((line) => line.seed !== null).length,
    hasActaBands: bands.length > 0,
  };
}

/** Texto de la celda "Banda" de una línea (compartido tabla en pantalla/PDF):
 * sembrados muestran su banda y si fue fija o sorteada; los demás "Sorteo"
 * cuando hubo acta con bandas (entraron 100% por sorteo) y "—" si no hay
 * acta o la línea está vacía. */
export function drawLineBandText(line: DrawCeremonyLine, hasActaBands: boolean): string {
  if (line.band !== null) return `${line.band} · ${line.bandDrawn ? "sorteada" : "fija"}`;
  if (line.matchNumber === null || line.displayName === null) return "—";
  return hasActaBands ? "Sorteo" : "—";
}

/** Texto de la celda "Jugador": BYE solo cuando el partido existe y el lugar
 * quedó vacío; una línea sin partido queda "Por definir". */
export function drawLinePlayerText(line: DrawCeremonyLine): string {
  if (line.matchNumber === null) return "Por definir";
  if (line.displayName !== null) return line.displayName;
  // Un lado vacío solo es BYE si el partido ES un BYE real (status "bye");
  // un lugar que espera al ganador de la preliminar/ronda parcial se rotula
  // "Por definir" — la UI no muestra BYEs estimados (gate de #135).
  return line.isBye ? "BYE" : "Por definir";
}

/**
 * Fingerprint del estado del cuadro para re-consultar el acta al regenerar
 * (gate de #135): contar partidos no basta — un rearmado del mismo tamaño
 * dejaba método/bandas/hora del acta stale. SIN hash: un DJB2 de 32 bits tuvo
 * una colisión REAL reproducida en el gate ([(410,cancelled),(1241,cancelled)]
 * vs [(215,bye),(2260,scheduled)]) — se usa el JSON íntegro de las tuplas
 * [id, status, updated_at] ordenadas por id (determinista y sin pérdida).
 */
export function drawCeremonyRefreshFingerprint(
  matches: Array<{ id: number; status?: string | null; updated_at?: string | null }>,
): string {
  const tuples = matches
    .map((match) => [match.id, match.status ?? "", match.updated_at ?? ""] as const)
    .sort((a, b) => a[0] - b[0]);
  return JSON.stringify(tuples);
}
